import React, { ReactElement, useState } from "react";
import { Select, Spin } from "antd";
import ZTypography from "../ZTypography";
import { ISelectProps, IOptionProps } from "./interface";
import "./index.less";
import { SelectValue } from "antd/lib/select";

const { Option, OptGroup } = Select;
const { ZText } = ZTypography;

const ZSelect = <T extends SelectValue>({
	loading = false,
	disabled = false,
	selectOptions = [],
	isOptionsGroup = false,
	optionsGroup = [],
	showDropdownLoader = false,
	isInputNumberSelect = false,
	className = "",
	zref,
	...rest
}: ISelectProps<T>): ReactElement => {
	const [inputSearchKeyword, setInputSearchKeyword] = useState<string>();

	const onKeyUp = (originalEvent: React.KeyboardEvent<HTMLDivElement>) => {
		const target = originalEvent.target as HTMLInputElement;
		const inputValue = target.value;

		if (!!inputValue?.length) {
			setInputSearchKeyword(inputValue);
		}

		rest.onKeyUp?.(originalEvent);
	};

	/* 
		Usecase: 3 letter search
		Description: There is a need to improve the search experience for options which are static i.e not being fetched from an API as the user types keyword(s).
		In most of the ZSelect instances, the search is broken.

		Here, we standardize the search with "3 letter" experience. The search ONLY takes place when the search keyword has 3 or more letters.
		If less, we show a UI indicating that more letters need to be entered. 

		We add a new prop called "useThreeLetterSearch" which enables it on ZSelect.
		By default, Antd performs an exact match using the filterOption prop. We leverage the same with 3 letter check on the function being passed as reference.
	*/
	const showSkeletonForSearch =
		rest.useThreeLetterSearch &&
		rest.showSearch &&
		typeof rest.filterOption === "function" &&
		inputSearchKeyword &&
		inputSearchKeyword.length < 3;

	const notFoundContent = showSkeletonForSearch ? (
		<div className="search-information">
			Please type atleast 3 letters to start searching
		</div>
	) : (
		rest.notFoundContent
	);

	return (
		<Select<T>
			ref={zref}
			loading={loading}
			disabled={disabled}
			bordered={isInputNumberSelect ? false : true}
			className={`z-select ${
				isInputNumberSelect
					? "addOnAfter rounded-none rounded-r-md border-0 border-t border-r border-b border-solid flex items-center text-center"
					: ""
			} ${className} ${rest.mode === "multiple" ? "multipleSelect" : ""}`}
			notFoundContent={notFoundContent}
			onKeyUp={onKeyUp}
			{...rest}
		>
			{showDropdownLoader && loading ? (
				<Option
					key="0000000"
					disabled
					value="0000000"
					className="m-auto"
				>
					<Spin spinning={true} />
				</Option>
			) : isOptionsGroup ? (
				optionsGroup.map(optGroup => (
					<OptGroup
						label={
							<div className="w-full truncate">
								<ZText level={0}>{optGroup.label}</ZText>
							</div>
						}
						key={optGroup.label}
					>
						{optGroup.selectOptions?.map(
							(option: IOptionProps | string | number) =>
								option instanceof Object ? (
									<Option
										disabled={option.disabled}
										key={option.key || option.value}
										data-testid={option.datatestid}
										{...option}
									>
										{isInputNumberSelect ? (
											<ZText level={0} colorVariant={7}>
												{option.name}
											</ZText>
										) : (
											option.name
										)}
									</Option>
								) : (
									<Option key={option} value={option}>
										{isInputNumberSelect ? (
											<ZText level={0} colorVariant={7}>
												{option}
											</ZText>
										) : (
											option
										)}
									</Option>
								)
						)}
					</OptGroup>
				))
			) : (
				selectOptions.map((option: IOptionProps | string | number) =>
					option instanceof Object ? (
						<Option
							disabled={option.disabled}
							key={option.key || option.value}
							data-testid={option.datatestid}
							{...option}
						>
							{isInputNumberSelect ? (
								<ZText level={0} colorVariant={7}>
									{option.name}
								</ZText>
							) : (
								option.name
							)}
						</Option>
					) : (
						<Option key={option} value={option}>
							{isInputNumberSelect ? (
								<ZText level={0} colorVariant={7}>
									{option}
								</ZText>
							) : (
								<ZText level={1}>{option}</ZText>
							)}
						</Option>
					)
				)
			)}
		</Select>
	);
};

export default ZSelect;
